// src/hooks/usePWA.js
// ─────────────────────────────────────────────────────────────
//  PWA registration, install prompt, offline detection
// ─────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';

export default function usePWA() {
  const [isOnline,       setIsOnline]       = useState(navigator.onLine);
  const [isInstallable,  setIsInstallable]  = useState(false);
  const [isInstalled,    setIsInstalled]    = useState(false);
  const [installPrompt,  setInstallPrompt]  = useState(null);
  const [offlineOrders,  setOfflineOrders]  = useState([]);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          console.log('✅ Service Worker registered');

          // Listen for messages from SW
          navigator.serviceWorker.addEventListener('message', (e) => {
            if (e.data.type === 'ORDER_QUEUED') {
              setOfflineOrders((prev) => [...prev, e.data.order]);
            }
            if (e.data.type === 'SYNC_ORDERS') {
              syncOfflineOrders();
            }
          });
        })
        .catch((err) => console.warn('SW registration failed:', err));
    }

    // Online / offline detection
    const handleOnline  = () => {
      setIsOnline(true);
      syncOfflineOrders();
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);

    // Install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    });

    // Already installed
    if (window.matchMedia('(display-mode: fullscreen)').matches
     || window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const installApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsInstallable(false);
    }
  };

  const syncOfflineOrders = async () => {
    // Retry queued orders when back online
    const queued = JSON.parse(localStorage.getItem('offline-queue') || '[]');
    if (!queued.length) return;

    const remaining = [];
    for (const item of queued) {
      try {
        await fetch(item.url, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(item.body),
        });
      } catch {
        remaining.push(item);
      }
    }
    localStorage.setItem('offline-queue', JSON.stringify(remaining));
    if (remaining.length === 0) setOfflineOrders([]);
  };

  return {
    isOnline,
    isInstallable,
    isInstalled,
    offlineOrders,
    installApp,
  };
}
